---
title: "Why your churn rate lies, and what to track *instead*."
excerpt: "A single churn percentage hides more than it reveals. Logo churn, revenue churn, and the cohort underneath each move in different directions, and the headline number can fall while the business gets weaker."
category: "Churn"
author: samuel
date: "2026-05-28"
readTime: "6 min read"
deck: "One churn percentage on a slide feels precise. It is usually the *least* informative number in the room."
answer: "A single churn percentage mixes logo churn, revenue churn and net revenue churn, so it can fall while the business weakens. Losing ten small accounts and one large one can show the same logo churn but very different revenue loss. Track revenue churn by cohort, the dollar value that left, and the reason for each cancellation."
takeaways:
  - "Logo churn counts customers, revenue churn counts money, and net revenue churn also subtracts expansion and can go negative."
  - "The same headline churn rate can hide very different revenue loss depending on which accounts left."
  - "Blended churn smears new and tenured customers together; splitting by cohort shows whether the problem is onboarding or a specific plan."
  - "Replace the single percentage with revenue churn by cohort, dollars churned, and a reason code for each cancellation."
---

Churn is the metric everyone quotes and almost nobody defines the same way. Ask three
people for last month's churn and you'll get three answers, because they're silently
measuring different things: lost logos, lost revenue, or lost revenue net of the
expansion that offset it. The headline percentage can drift down while the underlying
business gets weaker.

## Logo churn and revenue churn are different stories

Losing ten small accounts and losing one large one can produce the same logo churn and
wildly different revenue churn. A business that reports "3% monthly churn" without
saying which it means is reporting noise.

- **Logo churn** answers how many customers left. It's the leading indicator of
  product-market fit.
- **Revenue churn** answers how much money left. It's what your forecast actually runs
  on.
- **Net revenue churn** folds in expansion from the customers who stayed, and can even
  go negative, the healthiest place a subscription business can be.

> The headline churn rate tells you a number changed. It rarely tells you whether you
> should be worried.

## Averages hide the cohort that's leaving

A blended churn rate smears together your newest signups and your most tenured
accounts. Those two groups almost never churn at the same rate. When you split by
cohort, the real pattern usually appears: churn concentrated in the first ninety days
(an onboarding problem) or creeping up in a specific plan tier (a packaging problem).

## What to track instead

Replace the single percentage with three views you can act on: revenue churn by
cohort, the dollar value of what churned rather than the count, and the reason code
behind each cancellation. The percentage is a summary. The cohort and the cause are
the diagnosis.
